import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import BrandHeader from "../components/BrandHeader";
import { QUESTIONS } from "../data/questions";
import { createAudit, uploadAuditPdf } from "../lib/api";
import { pdfBlob } from "../lib/pdf";
import { useClientAuth } from "../lib/clientAuthContext";

const initialAnswers = {
  name: "",
  age: "", height_cm: "", weight_kg: "", sex: "female",
  avg_steps: "", steps_note: "",
  workouts: "", workouts_per_week: "",
  bed_time: "", wake_time: "", sleep_note: "",
  last_two_hours: "",
  sleep_hours: "",
  broken_sleep: "",
  energy_level: "",
  stress_level: "",
  eating_habits: "",
  emotional_eating: "",
  mindset_patterns: [],
  mindset_root: "",
  me_time_current: "",
  me_time_realistic: "",
  main_goal: "",
  pace: "",
};

function isStepValid(q, a) {
  switch (q.id) {
    case "basics":            return a.age && a.height_cm && a.weight_kg;
    case "avg_steps":         return a.avg_steps !== "";
    case "workouts":          return a.workouts.trim().length > 0 && a.workouts_per_week !== "";
    case "bed_wake":          return a.bed_time && a.wake_time;
    case "last_two_hours":    return a.last_two_hours.trim().length > 0;
    case "sleep_hours":       return !!a.sleep_hours;
    case "broken_sleep":      return !!a.broken_sleep;
    case "energy_level":      return !!a.energy_level;
    case "stress_level":      return !!a.stress_level;
    case "eating_habits":     return a.eating_habits.trim().length > 0;
    case "emotional_eating":  return !!a.emotional_eating;
    case "mindset_patterns":  return Array.isArray(a.mindset_patterns) && a.mindset_patterns.length === 2;
    case "mindset_root":      return !!a.mindset_root;
    case "me_time_current":   return a.me_time_current.trim().length > 0;
    case "me_time_realistic": return a.me_time_realistic.trim().length > 0;
    case "main_goal":         return a.main_goal.trim().length > 0;
    case "pace":              return !!a.pace;
    default:                  return true;
  }
}

export default function Audit() {
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState(initialAnswers);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { client } = useClientAuth();

  // Seed `answers.name` from the signed-in client's preferred_name so the
  // greeting header + the payload sent to /api/audits both stay populated
  // even though the name question is no longer visible.
  useEffect(() => {
    if (client?.preferred_name) {
      setAnswers((prev) => (prev.name ? prev : { ...prev, name: client.preferred_name }));
    }
  }, [client]);

  const total = QUESTIONS.length;
  const q = QUESTIONS[stepIdx];
  const progress = ((stepIdx) / total) * 100;
  const valid = useMemo(() => isStepValid(q, answers), [q, answers]);

  const update = (patch) => setAnswers((prev) => ({ ...prev, ...patch }));

  const next = async () => {
    if (!valid) return;
    if (stepIdx < total - 1) {
      setStepIdx(stepIdx + 1);
      return;
    }
    // Submit
    setSubmitting(true);
    try {
      const payload = {
        ...answers,
        age: parseInt(answers.age, 10),
        height_cm: parseFloat(answers.height_cm),
        weight_kg: parseFloat(answers.weight_kg),
        avg_steps: parseInt(answers.avg_steps, 10),
        workouts_per_week: parseInt(answers.workouts_per_week, 10) || 0,
      };
      const result = await createAudit(payload);
      // Generate the final PDF in the browser and persist it on the server,
      // so the coach can re-download the exact file at any time.
      try {
        const blob = await pdfBlob(result);
        await uploadAuditPdf(result.id, blob);
      } catch (pdfErr) {
        console.error("PDF persist failed (non-fatal):", pdfErr);
      }
      navigate(`/results/${result.id}`);
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong generating your roadmap. Please try again.");
      setSubmitting(false);
    }
  };

  const back = () => stepIdx > 0 && setStepIdx(stepIdx - 1);

  return (
    <div className="min-h-screen bg-brand-base flex flex-col">
      <Toaster richColors position="top-center" />
      <BrandHeader small />

      {/* progress */}
      <div className="px-6 md:px-12 lg:px-16 max-w-3xl mx-auto w-full">
        <div className="progress-track" data-testid="audit-progress">
          <div className="progress-indicator" style={{ width: `${progress + (100 / total)}%` }} />
        </div>
        <div className="flex items-center justify-between mt-3 label-eyebrow">
          <span data-testid="audit-step-label">{q.eyebrow}</span>
          {answers.name && <span className="normal-case tracking-normal font-medium text-brand-ink-muted">Hi, {answers.name}</span>}
        </div>
      </div>

      <main className="flex-1 px-6 md:px-12 lg:px-16 py-10 md:py-14 max-w-3xl mx-auto w-full">
        {submitting ? (
          <SubmittingScreen name={answers.name} />
        ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={q.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-tight" data-testid="question-title">
                {q.title}
              </h2>
              {q.helperHtml ? (
                <p
                  className="mt-4 text-brand-ink-muted text-base leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: q.helperHtml }}
                />
              ) : q.helper ? (
                <p className="mt-4 text-brand-ink-muted text-base leading-relaxed">{q.helper}</p>
              ) : null}

              <div className="mt-10">
                <QuestionInput q={q} answers={answers} update={update} onEnterAdvance={next} />
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </main>

      {!submitting && (
        <footer className="px-6 md:px-12 lg:px-16 pb-10">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <button
              onClick={back}
              disabled={stepIdx === 0}
              className="btn-ghost inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="audit-back-btn"
            >
              <ArrowLeft size={16} strokeWidth={1.8} /> Back
            </button>
            <button
              onClick={next}
              disabled={!valid}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="audit-next-btn"
            >
              {stepIdx === total - 1 ? "See my results" : "Continue"}
              <ArrowRight size={16} strokeWidth={2} />
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}

function SubmittingScreen({ name }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center py-24 text-center"
      data-testid="audit-submitting"
    >
      <Loader2 className="text-brand-primary animate-spin" size={36} strokeWidth={1.5} />
      <h2 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-8">
        Building your personalised roadmap{name ? `, ${name}` : ""}…
      </h2>
      <p className="mt-3 text-brand-ink-muted max-w-md">
        Crunching your numbers and choosing the habits that will move the needle for you.
        This takes about 20 seconds.
      </p>
    </motion.div>
  );
}

function QuestionInput({ q, answers, update, onEnterAdvance }) {
  const handleEnter = (e) => {
    if (e.key === "Enter" && !e.shiftKey && q.type !== "textarea" && q.type !== "workouts") {
      e.preventDefault();
      onEnterAdvance();
    }
  };

  switch (q.type) {
    case "text":
      return (
        <input
          type="text"
          className="input-warm text-lg"
          placeholder={q.placeholder}
          value={answers[q.id] || ""}
          onChange={(e) => update({ [q.id]: e.target.value })}
          onKeyDown={handleEnter}
          autoFocus
          data-testid={`q-${q.id}-input`}
        />
      );
    case "textarea":
      return (
        <textarea
          className="input-warm text-lg min-h-[140px]"
          placeholder={q.placeholder}
          value={answers[q.id] || ""}
          onChange={(e) => update({ [q.id]: e.target.value })}
          autoFocus
          data-testid={`q-${q.id}-input`}
        />
      );
    case "number":
      return (
        <div className="space-y-5">
          <input
            type="number"
            className="input-warm text-lg"
            placeholder={q.placeholder}
            value={answers[q.field] || ""}
            onChange={(e) => update({ [q.field]: e.target.value })}
            onKeyDown={handleEnter}
            autoFocus
            data-testid={`q-${q.field}-input`}
          />
          {q.extra && (
            <textarea
              className="input-warm min-h-[90px]"
              placeholder={q.extra.placeholder}
              value={answers[q.extra.id] || ""}
              onChange={(e) => update({ [q.extra.id]: e.target.value })}
              data-testid={`q-${q.extra.id}-input`}
            />
          )}
        </div>
      );
    case "choice":
      return (
        <div className="space-y-3" data-testid={`q-${q.id}-choices`}>
          {q.options.map((opt) => {
            const selected = answers[q.id] === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`choice-card ${selected ? "selected" : ""}`}
                onClick={() => update({ [q.id]: opt.value })}
                data-testid={`q-${q.id}-opt-${opt.value}`}
              >
                <span className="text-brand-ink">{opt.label}</span>
              </button>
            );
          })}
        </div>
      );
    case "choice_cards": {
      // Single-select cards with a label + descriptive body.
      return (
        <div className="space-y-3" data-testid={`q-${q.id}-choices`}>
          {q.options.map((opt) => {
            const selected = answers[q.id] === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                className={`choice-card block text-left w-full ${selected ? "selected" : ""}`}
                onClick={() => update({ [q.id]: opt.value })}
                data-testid={`q-${q.id}-opt-${opt.value}`}
              >
                <span className="block text-xs font-bold tracking-[0.18em] text-brand-primary">{opt.label}</span>
                <span className="block mt-2 text-brand-ink leading-relaxed">{opt.description}</span>
              </button>
            );
          })}
        </div>
      );
    }
    case "multi_choice_cards": {
      // Multi-select cards constrained to exactly `maxSelect` picks.
      const current = Array.isArray(answers[q.id]) ? answers[q.id] : [];
      const maxSelect = q.maxSelect || 2;
      const toggle = (v) => {
        const already = current.includes(v);
        if (already) {
          update({ [q.id]: current.filter((x) => x !== v) });
        } else if (current.length < maxSelect) {
          update({ [q.id]: [...current, v] });
        }
        // else: silently ignore extra picks so the UI reads as "cap reached"
      };
      return (
        <div data-testid={`q-${q.id}-choices`}>
          <p className="mb-3 text-sm text-brand-ink-muted" data-testid={`q-${q.id}-progress`}>
            {current.length} of {maxSelect} selected
          </p>
          <div className="space-y-3">
            {q.options.map((opt) => {
              const selected = current.includes(opt.value);
              const disabled = !selected && current.length >= maxSelect;
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  className={`choice-card block text-left w-full ${selected ? "selected" : ""} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                  onClick={() => toggle(opt.value)}
                  data-testid={`q-${q.id}-opt-${opt.value}`}
                >
                  <span className="block text-xs font-bold tracking-[0.18em] text-brand-primary">{opt.label}</span>
                  <span className="block mt-2 text-brand-ink leading-relaxed">{opt.description}</span>
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    case "basics":
      return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Age</label>
            <input type="number" className="input-warm" placeholder="42" value={answers.age}
                   onChange={(e) => update({ age: e.target.value })} data-testid="q-age-input" autoFocus />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Height (cm)</label>
            <input type="number" className="input-warm" placeholder="165" value={answers.height_cm}
                   onChange={(e) => update({ height_cm: e.target.value })} data-testid="q-height-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Weight (kg)</label>
            <input type="number" className="input-warm" placeholder="72" value={answers.weight_kg}
                   onChange={(e) => update({ weight_kg: e.target.value })} data-testid="q-weight-input" />
          </div>
        </div>
      );
    case "workouts":
      return (
        <div className="space-y-4">
          <textarea
            className="input-warm min-h-[110px]"
            placeholder="e.g. 30-min strength, moderate intensity"
            value={answers.workouts}
            onChange={(e) => update({ workouts: e.target.value })}
            autoFocus
            data-testid="q-workouts-input"
          />
          <div>
            <label className="label-eyebrow block mb-2">Sessions per week</label>
            <input type="number" min="0" max="14" className="input-warm" placeholder="e.g. 3"
                   value={answers.workouts_per_week}
                   onChange={(e) => update({ workouts_per_week: e.target.value })}
                   data-testid="q-workouts-per-week-input" />
          </div>
        </div>
      );
    case "bed_wake":
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <TimePicker
              label="Bed time"
              value={answers.bed_time}
              onChange={(v) => update({ bed_time: v })}
              testid="bed-time"
              autoFocus
            />
            <TimePicker
              label="Wake time"
              value={answers.wake_time}
              onChange={(v) => update({ wake_time: v })}
              testid="wake-time"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Sleep routine notes</label>
            <textarea
              className="input-warm min-h-[110px]"
              placeholder="Share anything around your sleep routine you feel is important for us to know e.g. work night shifts, massive difference to sleep schedule on certain days of the week and why etc."
              value={answers.sleep_note || ""}
              onChange={(e) => update({ sleep_note: e.target.value })}
              data-testid="q-sleep-note-input"
            />
          </div>
        </div>
      );
    default:
      return null;
  }
}

function TimePicker({ label, value, onChange, testid, autoFocus = false }) {
  // Parse the parent's "10:30 PM" string into parts for initial state only.
  const parseInitial = (v) => {
    const m = String(v || "").match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (m) return { hh: m[1], mm: m[2], period: m[3].toUpperCase() };
    return { hh: "", mm: "", period: "PM" };
  };
  const initial = parseInitial(value);
  const [hh, setHh] = useState(initial.hh);
  const [mm, setMm] = useState(initial.mm);
  const [period, setPeriod] = useState(initial.period);

  const emit = (h, m, p) => {
    const hh = String(h).replace(/^0+(?=\d)/, ""); // strip leading zeros: "06" -> "6"
    const hOk = /^([1-9]|1[0-2])$/.test(hh);
    const mOk = /^([0-5]\d)$/.test(m);
    onChange(hOk && mOk ? `${hh}:${m} ${p}` : "");
  };

  const handleHour = (e) => {
    const v = e.target.value.replace(/[^\d]/g, "").slice(0, 2);
    setHh(v);
    emit(v, mm, period);
  };
  const handleMinute = (e) => {
    const v = e.target.value.replace(/[^\d]/g, "").slice(0, 2);
    setMm(v);
    emit(hh, v, period);
  };
  const handleMinuteBlur = () => {
    if (mm.length === 1) {
      const padded = `0${mm}`;
      setMm(padded);
      emit(hh, padded, period);
    }
  };
  const handlePeriod = (p) => {
    setPeriod(p);
    emit(hh, mm, p);
  };

  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          className="input-warm w-20 text-center text-lg"
          placeholder="10"
          value={hh}
          onChange={handleHour}
          autoFocus={autoFocus}
          data-testid={`q-${testid}-hour-input`}
        />
        <span className="text-2xl text-brand-ink-muted leading-none">:</span>
        <input
          type="text"
          inputMode="numeric"
          maxLength={2}
          className="input-warm w-20 text-center text-lg"
          placeholder="30"
          value={mm}
          onChange={handleMinute}
          onBlur={handleMinuteBlur}
          data-testid={`q-${testid}-minute-input`}
        />
        <div className="flex ml-2 gap-1.5">
          {["AM", "PM"].map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => handlePeriod(p)}
              className={`px-3 py-2 rounded-full text-sm font-medium transition-all ${
                period === p
                  ? "bg-brand-primary text-white"
                  : "bg-white border border-brand-border text-brand-ink-muted hover:border-brand-primary"
              }`}
              data-testid={`q-${testid}-${p.toLowerCase()}-btn`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
